import React, { createContext, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

const STORAGE_KEY = 'wabenzy-theme'
const DARK_LINK_ID = 'antd-dark'
const DARK_HREF = '/antd.dark.css'

const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} })

// Apply the theme to the DOM: toggle the body class our dark overrides hang off, and layer antd's
// prebuilt dark stylesheet on top of the (always-loaded) light one only when dark is active. Kept
// as a standalone function so the early inline script in index.html can mirror it (no flash).
export const applyThemeToDom = theme => {
  const isDark = theme === 'dark'
  if (typeof document === 'undefined') return
  // Class lives on <html> (matches the early no-flash script in index.html).
  document.documentElement.classList.toggle('dark-theme', isDark)
  let link = document.getElementById(DARK_LINK_ID)
  if (isDark && !link) {
    link = document.createElement('link')
    link.id = DARK_LINK_ID
    link.rel = 'stylesheet'
    link.href = DARK_HREF
    document.head.appendChild(link)
  } else if (isDark && link && link !== document.head.lastElementChild) {
    // The early script in index.html adds this link while <head> is still being parsed, so the
    // app's own stylesheet — antd's LIGHT build among it — lands after it and wins every rule at
    // equal specificity: dark chrome, white cards. Moving the node to the end once the app is
    // running puts the dark build last, where it belongs. The early script still does its job:
    // the sheet is already fetched, so there is no flash. Re-appending an existing link does not
    // refetch it.
    document.head.appendChild(link)
  } else if (!isDark && link) {
    link.parentNode.removeChild(link)
  }
}

const readStored = () => {
  try {
    // Dark is the default — only an explicit stored 'light' opts out.
    return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark'
  } catch (e) {
    return 'dark'
  }
}

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(readStored)

  useEffect(() => {
    applyThemeToDom(theme)
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch (e) {
      /* ignore quota / privacy-mode errors */
    }
  }, [theme])

  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'))

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

ThemeProvider.propTypes = {
  children: PropTypes.node
}

export const useTheme = () => useContext(ThemeContext)
